import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase, goOffline, goOnline, ref, onValue } from 'firebase/database';
import { getFunctions } from 'firebase/functions';
import { getMessaging } from 'firebase/messaging';
import { getStorage } from 'firebase/storage';

// ─── Firebase connectivity state ───────────────────────────────────────────
// null = not yet determined, true = connected, false = disconnected
let _firebaseConnected = null;
let _connectedWaiters = [];
let _connectedChangeHandlers = [];

/** Returns true if Firebase is currently reachable, false if not.
 *  Falls back to navigator.onLine before the first .info/connected event fires. */
export function isFirebaseConnected() {
  return _firebaseConnected ?? navigator.onLine;
}

/** Resolves to true once Firebase confirms connectivity, or falls back to
 *  navigator.onLine after timeoutMs. Never resolves early on the initial false
 *  that Firebase fires before the connection is established. */
export function waitForFirebaseReady(timeoutMs = 3000) {
  if (_firebaseConnected === true) return Promise.resolve(true);
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      _connectedWaiters = _connectedWaiters.filter(fn => fn !== waiter);
      resolve(navigator.onLine); // timed-out: best-effort fallback
    }, timeoutMs);
    const waiter = () => {
      clearTimeout(timer);
      resolve(true);
    };
    _connectedWaiters.push(waiter);
  });
}

/** Subscribe to Firebase connectivity changes. Returns an unsubscribe function. */
export function onFirebaseConnectedChange(handler) {
  _connectedChangeHandlers.push(handler);
  return () => { _connectedChangeHandlers = _connectedChangeHandlers.filter(h => h !== handler); };
}

// The project comes from marinas/<project-id>.json at build time (firebaseConfig.js):
// every deployed site talks to exactly its own project.
import { firebaseConfig } from './firebaseConfig';

// Prevent multiple Firebase initializations
let app;
if (!getApps().length) {
    app = initializeApp(firebaseConfig);
} else {
    app = getApp(); // Use existing instance
}

const auth = getAuth(app);
const database = getDatabase(app);
const storage = getStorage(app);
const functions = getFunctions(app);
let messaging = null;
if ('Notification' in window && 'serviceWorker' in navigator) {
  try { messaging = getMessaging(app); } catch (e) { console.warn('[initFirebase] messaging unavailable:', e); }
}

const connectedRef = ref(database, ".info/connected");

onValue(connectedRef, (snapshot) => {
    const connected = snapshot.val() === true;
    const changed = _firebaseConnected !== null && _firebaseConnected !== connected;
    _firebaseConnected = connected;

    // Only resolve waiters when Firebase confirms connected = true.
    // The initial false event (fired before connection establishes) must not
    // resolve waiters early — they keep waiting for the true that follows.
    if (connected) {
        _connectedWaiters.forEach(fn => fn());
        _connectedWaiters = [];
    }

    // Notify change subscribers (but not on the very first event)
    if (changed) _connectedChangeHandlers.forEach(fn => fn(connected));
});

auth.onAuthStateChanged((user) => {
    if (!user) {
        console.warn("User is signed out.");
    }
});

// Debounce goOnline/goOffline events
let reconnectTimeout;
window.addEventListener("online", () => {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => goOnline(database), 2000);
});

window.addEventListener("offline", () => {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => goOffline(database), 2000);
});

export { app, auth, database, functions, messaging, storage };
